import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Radom, { IManagedPaymentMethodDetails, IManagedPaymentStatus, IPaymentLink, IPaymentLinkOrder, InputFieldWithValue } from '../api/Radom'
import Presale from '../components/Presale'
import LoadingPresale from '../components/LoadingPresale'
import { camelCase } from 'lodash'
import { createWeb3Modal } from '@web3modal/wagmi/react'
import { projectId, wagmiConfig } from '../util/Managed'
import { trackCheckoutFunnelStep } from '../util/CheckoutFunnel'
import PageLoadState from '../components/PageLoadState'

function PresaleView(): ReactElement {
  const paymentLinkId = window.location.pathname.split('/').filter(Boolean).pop() ?? ''
  if (typeof window !== 'undefined' && !(window as any).__W3M_INITIALIZED__) {
    createWeb3Modal({ wagmiConfig, projectId, enableAnalytics: true })
    ;(window as any).__W3M_INITIALIZED__ = true
  }
  const [isLoading, setIsLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const [paymentLinkData, setPaymentLinkData] = useState<IPaymentLink>()

  const [isPreProcessingPayment, setIsPreProcessingPayment] = useState(false)
  const [order, setOrder] = useState<IPaymentLinkOrder>()
  const [managedPaymentDetails, setManagedPaymentDetails] = useState<IManagedPaymentMethodDetails>()
  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus>()
  const trackedFunnelStepsRef = useRef<Set<string>>(new Set())

  const [inputFields, setInputFields] = useState<InputFieldWithValue[]>([])

  const [errorMessage, setErrorMessage] = useState<string>()
  const [loadErrorMessage, setLoadErrorMessage] = useState<string>()

  useEffect(() => {
    setLoadErrorMessage(undefined)
    Radom.getPaymentLink(paymentLinkId)
      .then(data => {
        setPaymentLinkData(data)

        if (!trackedFunnelStepsRef.current.has('viewed')) {
          trackedFunnelStepsRef.current.add('viewed')
          void trackCheckoutFunnelStep({
            sourceType: 'presale',
            sourceId: paymentLinkId,
            rail: 'crypto',
            step: 'viewed'
          })
        }

        if (data.inputFields) {
          const inputFields: InputFieldWithValue[] = []
          for (const input of data.inputFields) {
            const prefilledValue = searchParams.get(camelCase(input.inputLabel))
            inputFields.push({ ...input, value: prefilledValue ?? '' })
          }
          setInputFields(inputFields)
        }
      })
      .catch((err: any) => {
        setLoadErrorMessage(err?.response?.data?.error ?? err?.message ?? 'Unable to load this presale link.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  if (isLoading) {
    return <LoadingPresale />
  }

  if (loadErrorMessage) {
    return <PageLoadState
      title="Presale unavailable"
      message={loadErrorMessage}
      onRetry={async () => {
        window.location.reload()
      }}
    />
  }

  if (!paymentLinkData || paymentLinkData.products.length <= 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#111827' }}>
        This presale link is currently unavailable.
      </div>
    )
  }

  return <Presale
    checkoutData={{
      ...paymentLinkData,
      inputFields,
      products: paymentLinkData.products.map(p => {
        return {
          product: p,
          quantity: 1
        }
      })
    }}
    onCheckout={async ({ selectedMethod, tokenAmount }) => {
      try {
        setErrorMessage(undefined)
        setIsPreProcessingPayment(true)

        const orderRes = await Radom.createPaymentLinkOrder({
          paymentLinkId,
          paymentMethod: { managed: { method: selectedMethod } },
          orderData: [
            ...inputFields.map(c => {
              return {
                key: c.inputLabel,
                value: c.value.toString()
              }
            }),
            {
              key: 'Token amount',
              value: tokenAmount
            }
          ]
        })

        if (!trackedFunnelStepsRef.current.has('paymentInitiated')) {
          trackedFunnelStepsRef.current.add('paymentInitiated')
          void trackCheckoutFunnelStep({
            sourceType: 'presale',
            sourceId: paymentLinkId,
            rail: 'crypto',
            step: 'paymentInitiated',
            metadata: {
              network: selectedMethod.network,
              tokenAddress: selectedMethod.token
            }
          })
        }
        setOrder(orderRes)
        setIsPreProcessingPayment(false)

        if (orderRes.paymentMethodDetails.managed) {
          setManagedPaymentDetails(orderRes.paymentMethodDetails.managed)
        }

        const checkPaymentInterval = setInterval(() => {
          if (!orderRes.paymentMethodDetails.managed) {
            return
          }

          Radom.getManagedPaymentStatus(orderRes.paymentMethodDetails.managed?.paymentId)
            .then(res => {
              setPaymentStatus(res)

              if (res.status === 'paymentConfirmed') {
                if (!trackedFunnelStepsRef.current.has('paymentConfirmed')) {
                  trackedFunnelStepsRef.current.add('paymentConfirmed')
                  void trackCheckoutFunnelStep({
                    sourceType: 'presale',
                    sourceId: paymentLinkId,
                    rail: 'crypto',
                    step: 'paymentConfirmed'
                  })
                }
                if (window?.parent) {
                  window.parent.postMessage({
                    orderId: orderRes.id,
                    status: res.status
                  }, '*')
                }

                clearInterval(checkPaymentInterval)
              }

              if (res.status === 'failed') {
                if (!trackedFunnelStepsRef.current.has('paymentFailed')) {
                  trackedFunnelStepsRef.current.add('paymentFailed')
                  void trackCheckoutFunnelStep({
                    sourceType: 'presale',
                    sourceId: paymentLinkId,
                    rail: 'crypto',
                    step: 'paymentFailed'
                  })
                }
                clearInterval(checkPaymentInterval)
              }
            }).catch(() => {})
        }, 1000)
      } catch (err) {
        setErrorMessage(err.reason || err.message)
        setIsPreProcessingPayment(false)
      }
    }}
    order={order}
    managedPaymentDetails={managedPaymentDetails}
    errorMessage={errorMessage}
    isPreProcessingPayment={isPreProcessingPayment}
    paymentStatus={paymentStatus}
    onInputFieldsChange={setInputFields}
    isSuccess={false}
    successElement={<></>}
  />
}

export default PresaleView
